import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  fetchUserServices,
  getUserPracticeById,
  updateAppointment,
  calculateAppointment,
  fetchallUsersByPractices,
  getAppointmentsByPracticeIds,
  getAccountByPatientId,
  getPatientById,
  deleteAppointment,
  deleteAppointmentSeries,
} from "../../api";
import PatientWhatsAppConsent from "../WhatsApp/PatientWhatsAppConsent";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Alert from "react-bootstrap/Alert";
import Dropdown from "react-bootstrap/Dropdown";
import Table from "react-bootstrap/Table";
import Spinner from "react-bootstrap/Spinner";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { useNavigate } from "react-router-dom";
import { AddPatientSteps } from "../PatientsAccounts/AddPatientInfo";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";
import {
  useAppointmentStatuses,
  isTransitionAllowed,
} from "../../hooks/useAppointmentStatuses";
import "./EditAppointments.css";
// The history panel is an `.acc-list` surface, so it needs the shared
// design-system stylesheet — same as the Accounts modals that use it.
import "../Accounts/acc-list.css";
import { createIntakeSession, linkIntakeToAppointment, getIntakeSessionForStaff, getIntakeEnabledForPractice } from "../../intakeApi";
import ClinicalHistoryCompactPanel from "../ClinicalHistory/ClinicalHistoryCompactPanel";
import ClinicalHistoryFullPanel from "../ClinicalHistory/ClinicalHistoryFullPanel";
import ServiceLinePicker from "../Services/ServiceLinePicker";
import AppointmentTimeWarnings, {
  warningMessages,
} from "./AppointmentTimeWarnings";
import { userCanSeeClinicalHistory } from "../ClinicalHistory/access";

// Temporarily hidden — the intake-review UI needs refinement before we
// surface navigation to it from the Server Result section. Flip to true to
// re-enable the "Open Review Queue" button.
const SHOW_REVIEW_QUEUE_LINK = false;

// Colour for a workflow step that hasn't happened yet, in the header's icon
// cluster. Must stay legible on the header's #CFE2FF tint — the previous value
// WAS that tint, so incomplete steps became invisible and the completed ones
// looked randomly spaced.
const APPT_STEP_INACTIVE = "rgba(15, 52, 144, 0.25)";

export function EditAppointmentModal({
  show,
  onHide,
  event,
  resources,
  addNotification,
  practiceIds,
  setEvents,
}) {
  const [services, setServices] = useState([]);
  const [practices, setPractices] = useState([]);
  const [patientPracticeIds, setPatientPracticeIds] = useState([]);
  // Availability warnings for the current Start/End window, rendered directly
  // under those two inputs. They used to be an info banner above the modal
  // body, which sat too far from the fields that caused them.
  //
  // The suggested-alternative-times state that lived here went with it: the
  // backend stopped returning `suggestions` when it stopped trimming
  // appointments to fit, so the branch that set it could never run and nothing
  // ever rendered it.
  const [timeWarnings, setTimeWarnings] = useState([]);
  const { toast, showToast, hideToast } = useToast();
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [resourceList, setResourceList] = useState([]);
  const [selectedPracticeId, setSelectedPracticeId] = useState("");

  // The practice's own statuses — this file held two more copies of the list
  // (a lookup map for the header icon and an inline array for the picker).
  const {
    statusesFor,
    resolve: resolveStatus,
    typeOf: statusTypeOf,
    behaviourOf: statusBehaviourOf,
    // Keyed on the modal's practices; every lookup below passes the
    // appointment's own practiceId so a multi-practice user resolves correctly.
  } = useAppointmentStatuses(practiceIds);
  const [selectedResourceId, setSelectedResourceId] = useState("");
  const [filteredResources, setFilteredResources] = useState([]);
  const [closeAlert, setCloseAlert] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [account, setAccount] = useState({});
  const [modalShow, setModalShow] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const navigate = useNavigate();
  const [selectedLocationIdx, setSelectedLocationIdx] = useState(0);
  const [locationsForPractice, setLocationsForPractice] = useState([]);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteAlert, setDeleteAlert] = useState(false);
  const [deleteSeriesAlert, setDeleteSeriesAlert] = useState(false);
  const [showDeleteSubmenu, setShowDeleteSubmenu] = useState(false);
  const [showStatusSubmenu, setShowStatusSubmenu] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [intakeSession, setIntakeSession] = useState(null);
  const [sendIntakeEmail, setSendIntakeEmail] = useState(false);
  const [sendIntakeSMS, setSendIntakeSMS] = useState(false);
  const [intakeSending, setIntakeSending] = useState(false);
  const [intakeEnabled, setIntakeEnabled] = useState(false);
  const [intakeOutcomeOpen, setIntakeOutcomeOpen] = useState(false);
  const [intakeConsentOpen, setIntakeConsentOpen] = useState(false);
  const [intakeDataOpen, setIntakeDataOpen] = useState(false);
  const [intakeHistoryOpen, setIntakeHistoryOpen] = useState(false);
  const [reminderHistoryOpen, setReminderHistoryOpen] = useState(false);
  // Inline history view inside the Edit Appointment modal — replaces the body.
  // 'none' shows the appointment form; 'compact' / 'full' show the history.
  const [historyView, setHistoryView] = useState('none');
  const canSeeClinicalHistory = userCanSeeClinicalHistory();

  // 🔧 FIX: Separate refs for different initialization stages
  const isInitializedRef = useRef(false);
  const hasCalculatedEndRef = useRef(false);
  // Once the user hand-edits the End field, suppress the auto-recalc effect
  // so changing start/service/resource later doesn't snap end back to
  // start + service duration. Cleared on modal close.
  const endManuallyEditedRef = useRef(false);
  const locationInitializedRef = useRef(false);
  // Monotonic sequence for the account/patient fetch. If a later fetch starts
  // before an earlier one resolves (e.g. user switches appointments fast),
  // only the latest result is allowed to update state — prevents stale
  // patient/account data leaking into Create-Invoice navigation.
  const accountFetchSeqRef = useRef(0);

  const openedWithRef = useRef({ serviceId: "", practiceId: "", resourceId: "" });
  const [appointment, setAppointment] = useState({
    title: "",
    patientFirstName: "",
    patientLastName: "",
    start: "",
    end: "",
    patientId: "",
    serviceId: "",
    reminderInterval: [],
    reminderType: [],
    notes: "",
    resourceName: "",
    resourceId: "",
    practiceId: "",
    status: "",
    eventColor: "",
    patientEmail: "",
    patientPhone: "",
    borderColor: "",
    backgroundColor: "",
    appointmentCreatedBy: "",
    appointmentCreatedDate: "",
    appointmentConfirmationEmail: "",
    // Always default to false on EDIT: re-editing an appointment must not
    // silently re-send the original confirmation email regardless of the
    // user's auto-confirm preference (only the Add flow respects that flag).
    confirmEmail: false,
    recurringData: {},
  });

  // The picker must offer THIS appointment's practice's statuses. The modal is
  // opened from a calendar that merges every practice the user belongs to, so
  // the merged list would show one practice's renamed label and colour against
  // an appointment configured by another.
  const statusConfig = useMemo(
    () => statusesFor(appointment.practiceId),
    [statusesFor, appointment.practiceId]
  );

  // helpers
  const toNaiveLocal = (v) => {
    // Guard null/undefined/"" up front — `new Date(null)` is epoch (not NaN),
    // which would otherwise format as "1970-01-01T02:00" in SAST.
    if (v == null || v === "") return "";
    if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v))
      return v;
    const d = v instanceof Date ? v : new Date(v);
    if (isNaN(d)) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
    )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openAddPatientInfo = () => {
    onHide?.();
    setTimeout(() => {
      setModalData({
        patientFirstName: appointment.patientFirstName || "",
        patientLastName: appointment.patientLastName || "",
        patientEmail: appointment.patientEmail || "",
        patientPhone: appointment.patientPhone || "",
        appointmentId: appointment.id || event?.id || "",
      });
      setModalShow(true);
    }, 0);
  };

  const getExt = (e) => e?._def?.extendedProps ?? e?.extendedProps ?? {};
  const toId = (x) =>
    typeof x === "string" ? x : x?.id || x?._id || x?.userId || "";
  const isPractitioner = (u) =>
    Array.isArray(u.role)
      ? u.role.includes("Practitioner")
      : String(u.role || "").includes("Practitioner");

  const buildFallbackLocations = (p) => {
    if (Array.isArray(p?.locations) && p.locations.length) return p.locations;
    return [
      {
        name: p?.primaryLocationName || "Main Location",
        address1: p?.practiceAddress || "",
        address2: p?.practiceAddress2 || "",
        city: p?.practiceCity || "",
        province: p?.practiceProvince || "",
        postalCode: p?.practicePostalCode || "",
      },
    ];
  };

  const compactAddress = (loc = {}) =>
    [
      loc.address1,
      loc.address2,
      [loc.city, loc.province].filter(Boolean).join(", "),
      loc.postalCode,
    ]
      .filter(Boolean)
      .join(" | ");

  const toMinuteString = (v) => {
    if (!v) return "";
    if (typeof v === "string") {
      const m = v.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(?::\d{2})?$/);
      if (m) return m[1];
      const d2 = new Date(v);
      if (!isNaN(d2)) return toMinuteStringFromDate(d2);
      return "";
    }
    if (v instanceof Date && !isNaN(v)) return toMinuteStringFromDate(v);
    const d = new Date(v);
    return isNaN(d) ? "" : toMinuteStringFromDate(d);
  };

  const toMinuteStringFromDate = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
    )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const pickStartStr = (evt) => {
    const ext = getExt(evt);
    return toMinuteString(ext.start ?? evt.startStr ?? evt.start);
  };

  const pickEndStr = (evt) => {
    const ext = getExt(evt);
    const raw = ext.end ?? evt.endStr ?? evt.end;
    return raw ? toMinuteString(raw) : "";
  };

  // ============ 🔧 FIX: Single initialization effect with proper guards ============
  useEffect(() => {
    if (!show || !event) {
      // Reset all refs when modal closes
      isInitializedRef.current = false;
      hasCalculatedEndRef.current = false;
      endManuallyEditedRef.current = false;
      locationInitializedRef.current = false;
      setIsEditMode(false);
      setDeleteAlert(false);
      setShowStatusSubmenu(false);
      setActionsOpen(false);
      setIntakeSession(null);
      setSendIntakeEmail(false);
      setSendIntakeSMS(false);
      setReminderHistoryOpen(false);
      setPatientPracticeIds([]);
      setAccount({});
      return;
    }

    // Only initialize once per modal open
    if (isInitializedRef.current) return;

    const ext = getExt(event);
    const practiceId = ext.practiceId || event.practiceId || "";
    const resourceId =
      ext.resourceId ||
      event.resourceId ||
      (Array.isArray(event.resourceIds) ? event.resourceIds[0] : "");

    const startStr = pickStartStr(event);
    const endStr = pickEndStr(event);

    // 🔧 FIX: Preserve ALL location data from the event
    const eventLocation = ext.location ?? event.location ?? null;
    const eventLocationName = ext.locationName ?? event.locationName ?? "";
    const eventLocationAddress =
      ext.locationAddress ?? event.locationAddress ?? "";
    const eventLocationIdx =
      typeof ext.locationIdx === "number" ? ext.locationIdx : null;
    console.log("event", event);
    // What the appointment was opened with: the service-for-practice rule
    // applies only when this edit changes the service, practice or
    // practitioner, so routine edits of an older appointment are not blocked.
    openedWithRef.current = {
      serviceId: event.serviceId || "",
      practiceId: String(practiceId || ""),
      resourceId: String(resourceId || ""),
    };
    setAppointment({
      id: event.id || "",
      title: event.title || "",
      patientFirstName: event.patientFirstName || "",
      patientLastName: event.patientLastName || "",
      patientEmail: event.patientEmail || "",
      patientPhone: event.patientPhone || "",
      start: startStr,
      end: endStr,
      serviceId: event.serviceId || "",
      notes: event.notes || "",
      resourceId: resourceId,
      resourceName: ext.resourceName || event.resourceName || "",
      practiceId: practiceId,
      patientId: event.patientId || "",
      appointmentConfirmationEmail: false,
      confirmEmail: false,
      status: event.status || "Pending",
      backgroundColor:
        resolveStatus(event.status, event.practiceId).color,
      // 🔧 CRITICAL: Preserve location data exactly as it comes from backend
      location: eventLocation,
      locationName: eventLocationName,
      locationAddress: eventLocationAddress,
      locationIdx: eventLocationIdx,
      isInvoiced: event.isInvoiced || false,
      isInvoiceEmailed: event.isInvoiceEmailed || false,
      isPaid: event.isPaid || false,
      isNotesDone: event.isNotesDone || false,
      isRecurring: event.isRecurring || ext.isRecurring || false,
      recurringID: event.recurringID || ext.recurringID || "",
    });

    setSelectedResourceId(resourceId || "");
    setSelectedStatus(event.status || "Pending");

    // 🔧 Mark as initialized and that we already have an end time
    isInitializedRef.current = true;
    if (endStr) {
      hasCalculatedEndRef.current = true;
    }
  }, [show, event]);

  // Load linked intake session when modal opens (if one was sent for this appointment)
  useEffect(() => {
    if (!show || !event) return;
    const tokenId = event.intakeSessionTokenId;
    if (!tokenId) { setIntakeSession(null); return; }
    getIntakeSessionForStaff(tokenId)
      .then((data) => setIntakeSession(data))
      .catch(() => setIntakeSession(null));
  }, [show, event]);

  // Check if intake is enabled for this practice (T&C configured)
  useEffect(() => {
    const practiceId = event?.practiceId;
    if (!show || !practiceId) { setIntakeEnabled(false); return; }
    getIntakeEnabledForPractice(practiceId).then(setIntakeEnabled);
  }, [show, event?.practiceId]);

  // ============ 🔧 FIX: Fetch lists only once after initialization ============
  useEffect(() => {
    if (!show || !isInitializedRef.current) return;

    const practiceId = appointment.practiceId;
    const resourceId = appointment.resourceId;
    const patientId = appointment.patientId;

    // Clear patient-derived state up front so a stale `account` / practice
    // list from the previous appointment can't leak into Create-Invoice
    // navigation while this fetch is in flight.
    setAccount({});
    setPatientPracticeIds([]);

    // Bump the sequence so any earlier in-flight fetch's `setAccount`
    // call (resolving later than us) is ignored.
    const mySeq = ++accountFetchSeqRef.current;
    let isMounted = true;
    const isCurrent = () => isMounted && mySeq === accountFetchSeqRef.current;

    (async () => {
      try {
        if (practiceId) {
          const users = await fetchallUsersByPractices([practiceId]);
          if (!isCurrent()) return;

          const normalized = (users || []).map((u) => ({
            ...u,
            id: u.id || u._id,
          }));
          const practitioners = normalized.filter(isPractitioner);
          setResourceList(practitioners);
          setFilteredResources(practitioners); // 🔧 Set both lists
        }

        if (resourceId) {
          // includeDeleted: true so an existing appointment whose service was
          // soft-deleted can still resolve `services.find(...)` and show the
          // original name in the read-only chip. ServiceLinePicker filters
          // deleted out of the searchable dropdown so they're not re-selectable.
          const servicesList = await fetchUserServices(resourceId, {
            includeDeleted: true,
          });
          if (!isCurrent()) return;
          setServices(servicesList || []);
        }

        if (resourceId) {
          const practiceList = await getUserPracticeById(resourceId);
          if (!isCurrent()) return;
          setPractices(practiceList || []);
        }

        if (patientId) {
          const [account, patientDoc] = await Promise.all([
            getAccountByPatientId(patientId),
            getPatientById(patientId),
          ]);
          if (!isCurrent()) return;
          setAccount(account);
          if (Array.isArray(patientDoc?.patientPractice)) {
            setPatientPracticeIds(patientDoc.patientPractice.map(String));
          }
        }
      } catch (err) {
        console.error("❌ fetch lists error:", err);
      }
    })();

    return () => {
      isMounted = false;
    };
    // Re-run when the appointment changes within an open modal. Previously
    // this depended on `isInitializedRef.current` (a ref — not reactive),
    // so switching to a different appointment while the modal stayed open
    // left `account` bound to the previous patient. That stale value got
    // baked into the Create-Invoice URL and produced cross-account invoices.
  }, [show, appointment.patientId, appointment.practiceId, appointment.resourceId]);

  // Filter practices to only those the selected patient is linked to
  const filteredPractices = useMemo(() => {
    if (!patientPracticeIds.length) return practices;
    const linkedSet = new Set(patientPracticeIds);
    return practices.filter((p) => linkedSet.has(String(p.practiceNumber)));
  }, [practices, patientPracticeIds]);

  // ============ 🔧 REPLACE the location initialization useEffect with this ============
  useEffect(() => {
    if (!show || !isInitializedRef.current || locationInitializedRef.current)
      return;
    if (!practices.length || !appointment.practiceId) return;

    const p = practices.find(
      (pr) => pr.practiceNumber === appointment.practiceId
    );
    if (!p) return;

    const locs = buildFallbackLocations(p);
    setLocationsForPractice(locs);

    console.log("📍 Location Init:", {
      appointmentLocationName: appointment.location?.name,
      availableLocations: locs.map((l) => l.name),
      currentSelectedIdx: selectedLocationIdx,
    });
    console.log("event", event);

    // 🔧 KEY FIX: Find the index that matches the appointment's location
    // This is EXACTLY how resource/therapist works
    let matchedIdx = 0; // Default fallback

    if (appointment.locationName) {
      const found = locs.findIndex(
        (l) =>
          (l.name || "").trim().toLowerCase() ===
          appointment.locationName.trim().toLowerCase()
      );

      if (found >= 0) {
        matchedIdx = found;
        console.log("✅ Found matching location at index:", found);
      } else {
        console.warn(
          "⚠️ Location from backend not found in practice locations, using default"
        );
      }
    }

    // 🔧 CRITICAL: Set the index BUT DON'T modify appointment data
    // The appointment already has the correct location data from initial load
    setSelectedLocationIdx(matchedIdx);

    locationInitializedRef.current = true;
  }, [
    show,
    practices,
    appointment.practiceId,
    appointment.locationName,
    isInitializedRef.current,
  ]);


  // ============ 🔧 FIX: Recalculate end when user manually changes start/service/resource ============
  useEffect(() => {
    if (!show || !isInitializedRef.current) return;
    if (hasCalculatedEndRef.current) return; // 🔧 Don't recalculate if we already have end time (initial load)
    // A hand-edited end used to abort this effect outright, which also killed
    // the availability check — moving Start after overriding End produced no
    // warning at all. The override now guards only the write to `end` below.
    if (statusTypeOf(appointment.status, appointment.practiceId) === "Scheduling") return;
    if (!appointment.start || !appointment.serviceId || !appointment.resourceId)
      return;

    const svc = services.find((s) => s._id === appointment.serviceId);
    if (!svc?.servicesDuration) return;

    (async () => {
      try {
        const result = await calculateAppointment({
          appointment: {
            start: appointment.start,
            resourceId: appointment.resourceId,
            practitionerName: appointment.resourceName || "",
            appointmentId: appointment.id, // 🔧 ADD THIS
            // Lets the backend resolve conflicts against this practice's own
            // statuses instead of falling back to the seeded defaults.
            practiceId: appointment.practiceId,
          },
          selectedService: {
            servicesDuration: svc.servicesDuration,
            serviceId: svc._id,
          },
        });

        if (result?.endTime) {
          if (!endManuallyEditedRef.current) {
            const nextEnd =
              typeof result.endTime === "string"
                ? result.endTime
                : toNaiveLocal(result.endTime);

            setAppointment((prev) => ({ ...prev, end: nextEnd }));
          }
          hasCalculatedEndRef.current = true; // Mark as calculated
          setTimeWarnings(warningMessages(result.warnings));
        }
      } catch (e) {
        console.error("calc end error:", e);
      }
    })();
  }, [
    show,
    appointment.start,
    appointment.serviceId,
    appointment.resourceId,
    services,
  ]); // 🔧 React to changes BUT guard with hasCalculatedEndRef

  // ------------- handlers --------------

  const handleStartTimeChange = (e) => {
    setAppointment((prev) => ({ ...prev, start: e.target.value }));
    hasCalculatedEndRef.current = false; // Allow recalculation when user changes start
  };

  const handleEndTimeChange = (e) => {
    setAppointment((prevState) => ({ ...prevState, end: e.target.value }));
    // Mark end as user-overridden so the start/service/resource recalc effect
    // doesn't snap it back to start + duration.
    endManuallyEditedRef.current = true;
    hasCalculatedEndRef.current = true;
  };

  const handleChangeAppointment = (e) => {
    const { name, value } = e.target;
    setAppointment((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleServiceChange = (e) => {
    const selectedServiceId = e.target.value;
    setAppointment((prev) => ({ ...prev, serviceId: selectedServiceId }));
    hasCalculatedEndRef.current = false; // Allow recalculation when user changes service

    const svc = services.find((s) => s._id === selectedServiceId);
    if (svc?.servicesUser?.length) {
      const allowed = new Set(svc.servicesUser.map(toId));
      setFilteredResources(resourceList.filter((r) => allowed.has(toId(r))));
    } else {
      setFilteredResources(resourceList);
    }
  };

  // Only the practitioner's services that are set up for the appointment's
  // practice are offered. A service with no practices recorded is not offered
  // anywhere.
  const practiceServices = useMemo(() => {
    const practiceKey = String(appointment.practiceId || "");
    if (!practiceKey) return [];
    return services.filter(
      (s) =>
        Array.isArray(s.servicesPractice) &&
        s.servicesPractice.map(String).includes(practiceKey)
    );
  }, [services, appointment.practiceId]);

  function validateForm() {
    let newErrors = {};
    if (!appointment.title || appointment.title.trim() === "") {
      newErrors.title = "Title is required.";
    }
    if (!appointment.start) newErrors.start = "Start time is required.";
    if (!appointment.end) newErrors.end = "End time is required.";
    if (appointment.start && appointment.end) {
      const s = new Date(appointment.start).getTime();
      const e = new Date(appointment.end).getTime();
      if (!isNaN(s) && !isNaN(e) && s >= e) {
        newErrors.end = "End time must be after the start time.";
      }
    }
    if (!appointment.resourceId) {
      newErrors.resourceId = "Therapist selection is required.";
    }
    if (statusTypeOf(appointment.status, appointment.practiceId) !== "Scheduling") {
      if (!appointment.serviceId)
        newErrors.serviceId = "Service selection is required.";
      if (!appointment.practiceId)
        newErrors.practiceId = "Practice selection is required.";
      const opened = openedWithRef.current;
      const changed =
        appointment.serviceId !== opened.serviceId ||
        String(appointment.practiceId || "") !== opened.practiceId ||
        String(appointment.resourceId || "") !== opened.resourceId;
      if (
        changed &&
        appointment.serviceId &&
        !practiceServices.some((s) => s._id === appointment.serviceId)
      ) {
        newErrors.serviceId =
          "This service is not set up for the selected practice. Choose one of the practice's services.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSaveAppointment() {
    if (!appointment.start || !appointment.end || (statusTypeOf(appointment.status, appointment.practiceId) !== "Scheduling" && !appointment.serviceId)) {
      setErrorMessage("Start time, end time, and service are required.");
      return;
    }
    setErrorMessage("");
    if (!validateForm()) {
      console.error("Validation failed!");
      return;
    }

    const updatedAppointment = {
      ...appointment,
      recurringData: "",
      isRecurring: false,
      recurringID: "",
    };

    setIsLoading(true);
    // Whether the PUT itself succeeded, so the catch can tell a failed save
    // from a failed calendar refresh afterwards.
    let saved = false;
    try {
      // Send intake form if either checkbox is checked
      if ((sendIntakeEmail && appointment.patientEmail) || (sendIntakeSMS && appointment.patientPhone)) {
        setIntakeSending(true);
        try {
          const result = await createIntakeSession({
            practiceNumber: appointment.practiceId,
            patientName: [appointment.patientFirstName, appointment.patientLastName].filter(Boolean).join(" "),
            deliveryEmail: sendIntakeEmail ? appointment.patientEmail : null,
            deliverySMS: sendIntakeSMS ? appointment.patientPhone : null,
          });
          if (result.tokenId) {
            await linkIntakeToAppointment(result.tokenId, appointment.id);
            setIntakeSession({ ...result, status: "CREATED" });
          }
        } catch {
          // Non-fatal
        } finally {
          setIntakeSending(false);
          setSendIntakeEmail(false);
          setSendIntakeSMS(false);
        }
      }

      await updateAppointment(appointment.id, updatedAppointment);
      // From here the appointment IS saved; only the calendar refresh is left.
      saved = true;
      const updatedAppointments = await getAppointmentsByPracticeIds(
        practiceIds
      );
      console.log("✅ Updated Appointments Fetched:", updatedAppointments);

      const formattedEvents = updatedAppointments.map((evt) => {
        const id = evt.id || evt._id;

        // normalize location object for convenience
        const location = evt.location || {
          name: evt.locationName || "",
          address1: evt.locationAddress || "",
          address2: evt.locationAddress2 || "",
          city: evt.locationCity || "",
          province: evt.locationProvince || "",
          postalCode: evt.locationPostalCode || "",
        };

        return {
          // FC core fields
          id,
          title: evt.title,
          start: evt.start, // keep your DB "YYYY-MM-DDTHH:mm" strings
          end: evt.end,
          status: evt.status || "",

          // IMPORTANT for resource views:
          resourceId: evt.resourceId || undefined,
          resourceIds: evt.resourceId ? [evt.resourceId] : undefined,

          // optional visuals if you use them
          backgroundColor: evt.backgroundColor,
          borderColor: evt.borderColor,

          // everything else here
          extendedProps: {
            ...evt,
            startRaw: evt.start,
            endRaw: evt.end,
            location,
            patientEmail: evt.patientEmail,
            patientPhone: evt.patientPhone,
          },
        };
      });

      setEvents(formattedEvents);
      showToast("success", "Appointment successfully updated!");
      onHide();

      if (
        appointment.appointmentConfirmationEmail &&
        appointment.patientEmail
      ) {
        addNotification({
          message: `Updated confirmation email sent to ${appointment.patientEmail}`,
          timestamp: Date.now(),
          type: "email",
        });
      }
    } catch (error) {
      if (saved) {
        // The save went through; refreshing the calendar after it failed
        // (getAppointmentsByPracticeIds returns null on error). Never report
        // that as "NOT saved": a second Save would repeat the update and any
        // confirmation email.
        console.error("Calendar refresh after saving the appointment failed:", error);
        showToast("success", "Appointment successfully updated!");
        onHide();
        return;
      }
      console.error("Error updating appointment:", error);
      // Say why, with the server's reason (for example the service rule:
      // "This service is not set up for the selected practice"), and keep
      // the modal open so the user can correct it. A silent failure here
      // used to leave the form looking saved.
      const serverMessage =
        error?.response?.data?.error || error?.response?.data?.message;
      showToast(
        "error",
        `The appointment was NOT saved${serverMessage ? ` (${serverMessage})` : ""}. Your changes are still in the form.`
      );
    } finally {
      setIsLoading(false);
    }
  }

  function handleNavToInvoice() {
    const appt = appointment;
    // Guard: refuse to navigate while the patient's account is still being
    // resolved. Without this, a click during the fetch window would route to
    // a stale `account._id` from the previously-viewed appointment, filing
    // the invoice against the wrong account. Sanity-check the account is
    // tied to *this* appointment's patient before proceeding.
    const acctPatientIds = Array.isArray(account?.accountPatientId)
      ? account.accountPatientId.map(String)
      : [];
    if (!account?._id || !appt?.patientId || !acctPatientIds.includes(String(appt.patientId))) {
      showToast(
        "error",
        "Loading patient account — please wait a moment and try again."
      );
      return;
    }
    navigate(`/invoices/1/${account._id}`, {
      state: {
        servicesId: appt?.serviceId,
        appointmentId: appt?._id || appt?.id,
        appointmentStart: appt?.start || appt?.startTime || null,
        appointmentEnd: appt?.end || appt?.endTime || null,
        patientId: appt?.patientId,
        practiceId: appt?.practiceId || null,
        practitionerId:
          appt?.practitionerId || appt?.userId || appt?.resourceId || null,
      },
    });
  }

  function handleClose() {
    setAppointment({});
    onHide();
    setSelectedPracticeId("");
    setTimeWarnings([]);
    setIsEditMode(false);
    setDeleteAlert(false);
    setShowStatusSubmenu(false);
    setActionsOpen(false);
    setHistoryView('none');
  }

  const handleCancelNo = () => {
    setCloseAlert(false);
  };

  const handleCancelYes = () => {
    setCloseAlert(false);
    setAppointment({});
    onHide();
    setSelectedPracticeId("");
    setTimeWarnings([]);
    setIsEditMode(false);
    setDeleteAlert(false);
    setShowStatusSubmenu(false);
    setActionsOpen(false);
  };

  const handleStatusUpdate = async (newStatus) => {
    setIsUpdatingStatus(true);
    try {
      await updateAppointment(appointment.id, {
        ...appointment,
        status: newStatus,
        confirmEmail: false,
      });
      const updatedAppointments = await getAppointmentsByPracticeIds(
        practiceIds
      );

      const formattedEvents = updatedAppointments.map((evt) => ({
        ...evt,
        extendedProps: {
          ...evt,
          patientEmail: evt.patientEmail,
        },
      }));

      setEvents(formattedEvents);
      setSelectedStatus(newStatus);
      setAppointment((prev) => ({ ...prev, status: newStatus }));
      showToast("success", "The status has been updated!");
      setTimeout(() => {
        setIsUpdatingStatus(false);
      }, 2000);
    } catch (error) {
      console.error("Error updating status:", error);
      setIsUpdatingStatus(false);
    }
  };

  const handleNavToAppointment = () => {
    if (statusTypeOf(appointment.status, appointment.practiceId) === "NewPatient") {
      return navigate(`/newpatients/session/${appointment.id}`);
    } else {
      return navigate(
        `/patients/appointment/${appointment.patientId}/${appointment.id}`,
        { state: { collapsed: true } }
      );
    }
  };

  return (
    <div>
      <Modal
        show={show}
        onHide={handleClose}
        size="lg"
        centered
        scrollable
        dialogClassName="appointment-modal"
      >
       <Modal.Header
         closeButton={!closeAlert}
         style={{ background: "#CFE2FF" }}
         className="rounded-0 text-primary d-flex align-items-center"
       >
  <div className="appointment-modal-header d-flex align-items-center flex-nowrap w-100 gap-2" style={{ minWidth: 0 }}>
    {/* --- Title (shrinks with ellipsis) --- */}
    <Modal.Title as="h6" className="text-primary mb-0 text-truncate flex-shrink-1" style={{ minWidth: 0 }}>
      {appointment.title || "Edit Appointment"}
    </Modal.Title>

    {/* --- Cover badge (kept outside Modal.Title so superscript isn't clipped by text-truncate) ---
        Prefer category from the live patient doc (flowed via /appointments/availability)
        over the account doc, since older account records may not have the field saved. */}
    {(account?.accountIODFund || appointment?.patientIODFund) ? (
      (
        (appointment?.patientIODFundCategory || account?.accountIODFundCategory || "")
          .toUpperCase() === "INSURANCE"
      ) ? (
        <span className="badge bg-info text-dark flex-shrink-0" title="INSURANCE" style={{ fontSize: "0.55rem", alignSelf: "flex-start" }}>
          I
        </span>
      ) : (
        <span className="badge bg-warning text-dark flex-shrink-0" title="IOD" style={{ fontSize: "0.55rem", alignSelf: "flex-start" }}>
          I
        </span>
      )
    ) : (account?.accountMedicalAid || appointment?.patientMedicalAid) ? (
      <span className="badge bg-primary flex-shrink-0" title="Medical Aid" style={{ fontSize: "0.55rem", alignSelf: "flex-start" }}>
        M
      </span>
    ) : account?._id ? (
      <span className="badge bg-secondary flex-shrink-0" title="Private" style={{ fontSize: "0.55rem", alignSelf: "flex-start" }}>
        P
      </span>
    ) : null}

    {/* --- Icon cluster (never wraps, never shrinks) --- */}
    <div className="appointment-status-icons d-flex align-items-center flex-nowrap flex-shrink-0">
      {(() => {
        // resolve() always returns something renderable, so an unrecognised
        // status shows its own icon instead of a blank space with no tooltip.
        const s = resolveStatus(appointment.status, appointment.practiceId);
        return (
          <i
            className={`fa-solid ${s.icon}`}
            style={{ color: s.color }}
            title={s.label}
          ></i>
        );
      })()}

      <i
        className="fa-solid fa-rotate"
        title={appointment?.isRecurring ? "Recurring appointment" : "Not recurring"}
        style={{ color: appointment?.isRecurring ? "#0f3490" : APPT_STEP_INACTIVE }}
      ></i>

      <span className="appointment-status-divider"></span>

      <i
        className="fa-light fa-file-circle-info"
        title={intakeSession ? "Intake form sent" : "No intake form sent"}
        style={{ color: intakeSession ? "#0f3490" : APPT_STEP_INACTIVE }}
      ></i>
      <i
        className="fa-solid fa-clipboard"
        title={appointment?.isNotesDone ? "Notes completed" : "Notes not done"}
        style={{ color: appointment?.isNotesDone ? "#0f3490" : APPT_STEP_INACTIVE }}
      ></i>
      <i
        className="fa-solid fa-file-invoice"
        title={appointment?.isInvoiced ? "Invoiced" : "Not invoiced"}
        style={{ color: appointment?.isInvoiced ? "#0f3490" : APPT_STEP_INACTIVE }}
      ></i>
      <i
        className="fa-solid fa-envelope"
        title={appointment?.isInvoiceEmailed ? "Invoice emailed" : "Invoice not emailed"}
        style={{ color: appointment?.isInvoiceEmailed ? "#0f3490" : APPT_STEP_INACTIVE }}
      ></i>
      <i
        className="fa-solid fa-credit-card"
        title={appointment?.isPaid ? "Paid" : "Not paid"}
        style={{ color: appointment?.isPaid ? "#0f3490" : APPT_STEP_INACTIVE }}
      ></i>
    </div>

    {/* --- Right side: Actions dropdown --- */}
    <div className="ms-auto flex-shrink-0 me-4">
      <Dropdown
        show={actionsOpen}
        onToggle={(isOpen) => {
          setActionsOpen(isOpen);
          if (!isOpen) { setShowStatusSubmenu(false); setShowDeleteSubmenu(false); }
        }}
      >
        <Dropdown.Toggle variant="primary" size="sm" id="actions-dropdown">
          <i className="fa-solid fa-gears" />
          <span className="d-none d-md-inline ms-1">Actions</span>
        </Dropdown.Toggle>
        <Dropdown.Menu align="end" className="shadow-sm">
          <Dropdown.Item onClick={() => { setIsEditMode(true); setActionsOpen(false); }}>
            <i className="fa fa-edit me-2"></i> Edit
          </Dropdown.Item>
          {appointment?.patientId && canSeeClinicalHistory && (
            <Dropdown.Item
              onClick={() => { setHistoryView('compact'); setActionsOpen(false); }}
            >
              <i className="fa-solid fa-clock-rotate-left me-2"></i> History
            </Dropdown.Item>
          )}
          <Dropdown.Item onClick={(e) => { e.stopPropagation(); setShowStatusSubmenu(!showStatusSubmenu); }}>
            <i className="fa fa-refresh me-2"></i> Update Status
            <i className={`fa-solid fa-chevron-${showStatusSubmenu ? "up" : "down"} ms-2`} style={{ fontSize: "0.7rem" }}></i>
          </Dropdown.Item>
          {showStatusSubmenu && statusConfig.map(({ key, label, icon, color, type }) => {
            const isCurrent = appointment.status === key;
            // Same rule table as the day-sheet popover, shared and keyed on type.
            const isDisabled =
              !isCurrent &&
              !isTransitionAllowed(
                statusTypeOf(appointment.status, appointment.practiceId),
                type,
                (t) =>
                  statusBehaviourOf(
                    statusConfig.find((x) => x.type === t)?.key || appointment.status,
                    appointment.practiceId
                  )
              );
            return (
              <Dropdown.Item
                key={key}
                active={isCurrent}
                disabled={isDisabled && !isCurrent}
                style={{ paddingLeft: "2rem", fontSize: "0.9rem" }}
                onClick={() => {
                  if (isDisabled && !isCurrent) return;
                  handleStatusUpdate(key);
                  setActionsOpen(false);
                  setShowStatusSubmenu(false);
                }}
              >
                {/* The active item paints a solid brand-blue row, so the
                    status's own colour would sit unreadably on top of it —
                    the icon follows the label to white. */}
                <i
                  className={`fa-solid ${icon} me-2`}
                  style={{ color: isCurrent ? "#fff" : color }}
                ></i>
                {label}
              </Dropdown.Item>
            );
          })}
          {statusTypeOf(appointment.status, appointment.practiceId) === "NewPatient" && (
              <Dropdown.Item onClick={() => { openAddPatientInfo(); setActionsOpen(false); }}>
                <i className="fa-solid fa-user-plus me-2"></i> Add Patient Info
              </Dropdown.Item>
            )}
          {statusTypeOf(appointment.status, appointment.practiceId) !== "Scheduling" &&
            statusBehaviourOf(appointment.status, appointment.practiceId)
              .allowsInvoicing &&
            !appointment.isInvoiced &&
            appointment.practiceId &&
            appointment.serviceId &&
            selectedResourceId && (
              <Dropdown.Item onClick={() => { handleNavToInvoice(); setActionsOpen(false); }}>
                <i className="fa-solid fa-file-invoice-dollar me-2"></i> Create Invoice
              </Dropdown.Item>
            )}
          {statusBehaviourOf(appointment.status, appointment.practiceId)
            .allowsInvoicing &&
            appointment.practiceId &&
            appointment.serviceId &&
            selectedResourceId && (
              <Dropdown.Item onClick={() => { handleNavToAppointment(); setActionsOpen(false); }}>
                <i className="fa-solid fa-pen-to-square me-2"></i> Go To Session
              </Dropdown.Item>
            )}
          <Dropdown.Divider />
          <Dropdown.Item onClick={(e) => { e.stopPropagation(); setShowDeleteSubmenu(!showDeleteSubmenu); }}>
            <i className="fa fa-trash me-2"></i> Delete
            <i className={`fa-solid fa-chevron-${showDeleteSubmenu ? "up" : "down"} ms-2`} style={{ fontSize: "0.7rem" }}></i>
          </Dropdown.Item>
          {showDeleteSubmenu && (
            <>
              <Dropdown.Item
                style={{ paddingLeft: "2rem", fontSize: "0.9rem" }}
                disabled={appointment.isNotesDone || appointment.isInvoiced || appointment.isInvoiceEmailed || appointment.isPaid}
                onClick={() => { setDeleteAlert(true); setActionsOpen(false); setShowDeleteSubmenu(false); }}
              >
                <i className="fa-solid fa-calendar-xmark me-2"></i> This appointment
              </Dropdown.Item>
              {appointment.isRecurring && appointment.recurringID && (
                <Dropdown.Item
                  style={{ paddingLeft: "2rem", fontSize: "0.9rem" }}
                  disabled={appointment.isNotesDone || appointment.isInvoiced || appointment.isInvoiceEmailed || appointment.isPaid}
                  onClick={() => { setDeleteSeriesAlert(true); setActionsOpen(false); setShowDeleteSubmenu(false); }}
                >
                  <i className="fa-solid fa-calendar-minus me-2"></i> This & future in series
                </Dropdown.Item>
              )}
            </>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>

  </div>
</Modal.Header>


        {closeAlert && (
          <Alert
            variant="info"
            className="w-100 d-flex align-items-center justify-content-between mx-0 mb-0 border-0 rounded-0"
          >
            <span>
              Are you sure you want to close? The entered data will be removed.
            </span>
            <div className="d-flex gap-2">
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Discard and close</Tooltip>}
              >
                <Button variant="outline-primary" onClick={handleCancelYes}>
                  <i className="fa-solid fa-check" />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Keep editing</Tooltip>}
              >
                <Button variant="outline-secondary" onClick={handleCancelNo}>
                  <i className="fa-solid fa-xmark" />
                </Button>
              </OverlayTrigger>
            </div>
          </Alert>
        )}
        {errorMessage && (
          <Alert variant="danger" className="mx-0 mb-0 border-0 rounded-0">
            {errorMessage}
          </Alert>
        )}
        {deleteAlert && (
          <Alert
            variant="danger"
            className="w-100 d-flex align-items-center justify-content-between mx-0 mb-0 border-0 rounded-0"
          >
            <span>Are you sure you want to delete this appointment? This action cannot be undone.</span>
            <div className="d-flex gap-2">
              <Button
                variant="danger"
                disabled={isDeleting}
                style={{ minWidth: 140 }}
                onClick={async () => {
                  setIsDeleting(true);
                  try {
                    await deleteAppointment(appointment.id);
                    const updatedAppointments = await getAppointmentsByPracticeIds(practiceIds);
                    const formattedEvents = updatedAppointments.map((evt) => ({
                      ...evt,
                      extendedProps: {
                        ...evt,
                        patientEmail: evt.patientEmail,
                        patientPhone: evt.patientPhone,
                      },
                    }));
                    setEvents(formattedEvents);
                    setDeleteAlert(false);
                    showToast("success", "Appointment deleted successfully.");
                    onHide();
                  } catch (error) {
                    console.error("Error deleting appointment:", error);
                    showToast("error", "Failed to delete appointment.");
                  } finally {
                    setIsDeleting(false);
                  }
                }}
              >
                {isDeleting ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  <>
                    <i className="fa-solid fa-trash me-2" />
                    Delete
                  </>
                )}
              </Button>
              <Button
                variant="outline-secondary"
                style={{ minWidth: 140 }}
                onClick={() => setDeleteAlert(false)}
              >
                Cancel
              </Button>
            </div>
          </Alert>
        )}

        {deleteSeriesAlert && (
          <Alert
            variant="danger"
            className="w-100 d-flex align-items-center justify-content-between mx-0 mb-0 border-0 rounded-0"
          >
            <span>Delete this and all future appointments in this series? Appointments with notes, invoices, or payments will be skipped.</span>
            <div className="d-flex gap-2">
              <Button
                variant="danger"
                disabled={isDeleting}
                style={{ minWidth: 140 }}
                onClick={async () => {
                  setIsDeleting(true);
                  try {
                    const result = await deleteAppointmentSeries(appointment.recurringID, appointment.start);
                    const updatedAppointments = await getAppointmentsByPracticeIds(practiceIds);
                    const formattedEvents = updatedAppointments.map((evt) => ({
                      ...evt,
                      extendedProps: {
                        ...evt,
                        patientEmail: evt.patientEmail,
                        patientPhone: evt.patientPhone,
                      },
                    }));
                    setEvents(formattedEvents);
                    setDeleteSeriesAlert(false);
                    if (result.skippedCount > 0) {
                      showToast("warning", `Deleted ${result.deletedCount} appointment(s). ${result.skippedCount} skipped (have notes, invoices, or payments).`);
                    } else {
                      showToast("success", `Deleted ${result.deletedCount} appointment(s) from the series.`);
                    }
                    onHide();
                  } catch (error) {
                    console.error("Error deleting appointment series:", error);
                    showToast("error", "Failed to delete appointment series.");
                  } finally {
                    setIsDeleting(false);
                  }
                }}
              >
                {isDeleting ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  <>
                    <i className="fa-solid fa-calendar-minus me-2" />
                    Delete series
                  </>
                )}
              </Button>
              <Button
                variant="outline-secondary"
                style={{ minWidth: 140 }}
                onClick={() => setDeleteSeriesAlert(false)}
              >
                Cancel
              </Button>
            </div>
          </Alert>
        )}

        <Modal.Body style={{ position: 'relative', minHeight: '400px' }}>
  {historyView === 'compact' && (
    <ClinicalHistoryCompactPanel
      active
      patientId={appointment?.patientId}
      practiceId={appointment?.practiceId}
      patientName={
        [appointment?.patientFirstName, appointment?.patientLastName]
          .filter(Boolean)
          .join(" ")
          .trim() || appointment?.title || ""
      }
      onBack={() => setHistoryView('none')}
      onShowFull={() => setHistoryView('full')}
    />
  )}
  {historyView === 'full' && (
    <ClinicalHistoryFullPanel
      active
      patientId={appointment?.patientId}
      practiceId={appointment?.practiceId}
      patientName={
        [appointment?.patientFirstName, appointment?.patientLastName]
          .filter(Boolean)
          .join(" ")
          .trim() || appointment?.title || ""
      }
      onBack={() => setHistoryView('compact')}
    />
  )}
  {historyView === 'none' && (<>
  {isUpdatingStatus && (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        borderRadius: '0.375rem'
      }}
    >
      <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
        <span className="visually-hidden">Loading...</span>
      </div>
      <p className="mt-3 fw-bold text-primary">Updating status...</p>
    </div>
  )}
  <Form>
            {/* Title and Therapist share a row — Therapist used to sit alone
                further down, which cost a full field of height. */}
            <Row className="mb-2 g-2">
              <Form.Group as={Col} md={6}>
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={appointment.title}
                  onChange={handleChangeAppointment}
                  readOnly={!isEditMode}
                  className={!isEditMode ? "appt-readonly" : undefined}
                  isInvalid={!!errors.title}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.title}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group as={Col} md={6} controlId="appointmentResource">
                <Form.Label>Therapist</Form.Label>
                <Form.Select
                  name="resourceId"
                  value={selectedResourceId}
                  disabled={!isEditMode}
                  className={!isEditMode ? "appt-readonly" : undefined}
                  onChange={(e) => {
                    const newResourceId = e.target.value;
                    setSelectedResourceId(newResourceId);
                    const picked = resourceList.find(
                      (r) => toId(r) === newResourceId
                    );
                    setAppointment((prev) => ({
                      ...prev,
                      resourceId: newResourceId,
                      resourceName: picked
                        ? `${picked.firstName} ${picked.lastName}`
                        : prev.resourceName,
                    }));
                    hasCalculatedEndRef.current = false; // Allow recalculation
                  }}
                  isInvalid={!!errors.resourceId}
                >
                  <option value="" disabled>
                    Select a Therapist
                  </option>
                  {filteredResources.length > 0 ? (
                    filteredResources.map((resource) => (
                      <option key={toId(resource)} value={toId(resource)}>
                        {resource.firstName} {resource.lastName}
                      </option>
                    ))
                  ) : (
                    <option disabled>No Available Therapists</option>
                  )}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.resourceId}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>
            <Row className="mb-2 g-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Start</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="start"
                    value={appointment.start}
                    onChange={handleStartTimeChange}
                    readOnly={!isEditMode}
                    className={!isEditMode ? "appt-readonly" : undefined}
                    isInvalid={!!errors.start}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.start}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>End</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="end"
                    value={appointment.end}
                    onChange={isEditMode ? handleEndTimeChange : undefined}
                    readOnly={!isEditMode}
                    className={!isEditMode ? "appt-readonly" : undefined}
                    isInvalid={!!errors.end}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.end}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            {/* Sits under both time fields, spanning the row, because a
                warning can be caused by either one. */}
            <AppointmentTimeWarnings messages={timeWarnings} />
            {statusTypeOf(appointment.status, appointment.practiceId) !== "Scheduling" && (
              <div>
                <Row className="mb-2">
                  <Col>
                    <Form.Group>
                      <Form.Label>Service</Form.Label>
                      {/* Replaces the legacy Form.Select dropdown with the
                          shared ServiceLinePicker search experience. We pass
                          hideCustomCodes so the toggle to custom codes
                          doesn't appear — appointments only ever deal with
                          services. handleServiceChange expects a synthetic
                          event with target.name/value so the original
                          recalc-end side-effects keep working unchanged. */}
                      <ServiceLinePicker
                        services={practiceServices}
                        hideCustomCodes
                        isInvalid={!!errors.serviceId}
                        disabled={!isEditMode}
                        selectedServiceName={
                          services.find(
                            (s) => s._id === appointment.serviceId
                          )?.servicesName || ""
                        }
                        onClear={() =>
                          handleServiceChange({
                            target: { name: "serviceId", value: "" },
                          })
                        }
                        onSelect={(svcId) =>
                          handleServiceChange({
                            target: { name: "serviceId", value: svcId },
                          })
                        }
                      />
                      {errors.serviceId && (
                        <div className="text-danger small mt-1">
                          {errors.serviceId}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            )}
            <Row className="mb-2">
              <Form.Group as={Col} controlId="appointmentConfirmationEmail">
                <Form.Check
                  type="checkbox"
                  label="Send confirmation email"
                  name="appointmentConfirmationEmail"
                  checked={appointment.appointmentConfirmationEmail}
                  disabled={!isEditMode}
                  onChange={(e) => {
                    const { name, checked } = e.target;
                    handleChangeAppointment({
                      target: { name, value: checked },
                    });
                  }}
                />
              </Form.Group>
            </Row>
            <Row className="mb-2">
              <Form.Group controlId="appointmentNotes">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="notes"
                  value={appointment.notes}
                  onChange={handleChangeAppointment}
                  readOnly={!isEditMode}
                  className={!isEditMode ? "appt-readonly" : undefined}
                />
              </Form.Group>
            </Row>
            {statusTypeOf(appointment.status, appointment.practiceId) !== "Scheduling" && (
              <>
              <Row className="mb-2 g-2">
                <Form.Group as={Col} md={6} controlId="appointmentPractice">
                  <Form.Label>Practice</Form.Label>
                  <Form.Select
                    name="practice"
                    value={appointment.practiceId}
                    disabled={!isEditMode}
                    className={!isEditMode ? "appt-readonly" : undefined}
                    onChange={(e) => {
                      const practiceId = e.target.value;
                      const selectedPractice = filteredPractices.find(
                        (pr) => pr.practiceNumber === practiceId
                      );

                      setSelectedPracticeId(practiceId);
                      setAppointment((prev) => ({
                        ...prev,
                        practiceId: practiceId,
                        practiceName: selectedPractice
                          ? selectedPractice.practiceName
                          : "",
                      }));

                      const locs = buildFallbackLocations(
                        selectedPractice || {}
                      );
                      setLocationsForPractice(locs);
                      const idx = 0;
                      setSelectedLocationIdx(idx);

                      const first = locs[idx] || {};
                      setAppointment((prev) => ({
                        ...prev,
                        location: { ...first },
                        locationName:
                          first.name ||
                          selectedPractice?.primaryLocationName ||
                          "Main Location",
                        locationAddress: compactAddress(first),
                        locationIdx: idx,
                      }));
                    }}
                    isInvalid={!!errors.practiceId}
                  >
                    <option value="" disabled>
                      Select a Practice
                    </option>
                    {filteredPractices && filteredPractices.length > 0 ? (
                      filteredPractices.map((practice) => (
                        <option
                          key={practice.practiceNumber}
                          value={practice.practiceNumber}
                        >
                          {practice.practiceName}
                        </option>
                      ))
                    ) : (
                      <option disabled>No Practices Available</option>
                    )}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.practiceId}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} md={6} controlId="appointmentLocation">
                  <Form.Label>Location</Form.Label>
                  <Form.Select
                    name="locationIdx"
                    value={String(selectedLocationIdx)}
                    onChange={(e) => {
                      const idx = Number(e.target.value);
                      const loc = locationsForPractice[idx] || {};
                      setSelectedLocationIdx(idx);

                      setAppointment((prev) => ({
                        ...prev,
                        location: { ...loc },
                        locationName:
                          loc.name ||
                          filteredPractices.find(
                            (x) => x.practiceNumber === prev.practiceId
                          )?.primaryLocationName ||
                          "Location",
                        locationAddress: compactAddress(loc),
                        locationIdx: idx,
                      }));
                    }}
                    disabled={!isEditMode || locationsForPractice.length === 0}
                    className={!isEditMode ? "appt-readonly" : undefined}
                  >
                    {locationsForPractice.length > 0 ? (
                      locationsForPractice.map((loc, idx) => (
                        <option key={`loc-${idx}`} value={String(idx)}>
                          {(loc.name || "Location") +
                            " — " +
                            [loc.address1, loc.city, loc.province]
                              .filter(Boolean)
                              .join(", ")}
                        </option>
                      ))
                    ) : (
                      <option value="0">No Location</option>
                    )}
                  </Form.Select>
                  {/* 🔧 Show warning if location doesn't match any in the list */}
                  {appointment.location &&
                    appointment.location.name &&
                    locationsForPractice.length > 0 &&
                    !locationsForPractice.some(
                      (l) =>
                        l.name === appointment.location.name &&
                        l.address1 === appointment.location.address1
                    ) && <div className="form-text text-warning"></div>}
                  {(!appointment.location || !appointment.location.name) && (
                    <div className="form-text">
                      Defaults to the first location (primary).
                    </div>
                  )}
                </Form.Group>
              </Row>

              {/* WhatsApp consent sits with the communications part of the
                  modal. Status only — the record lives on the patient. */}
              <Row className="mb-2">
                <Col>
                  <PatientWhatsAppConsent patientId={appointment?.patientId} />
                </Col>
              </Row>

              {/* History row */}
              <Row className="mb-3">
                <Col>
                  <Form.Label className="fw-semibold">History</Form.Label>

                  {/* History container */}
                  {(event?.reminderLog?.length > 0 || intakeSession) ? (
                    <div className="acc-list small mb-2">

                      {/* Reminder log */}
                      {event?.reminderLog?.length > 0 && (
                        <div className="px-2 pb-2">
                          <Table bordered responsive size="sm" className="mb-0 mt-2 align-middle">
                            <thead
                              style={{ cursor: "pointer", userSelect: "none" }}
                              onClick={() => setReminderHistoryOpen((o) => !o)}
                            >
                              <tr>
                                <th className="text-primary fw-semibold" style={{ width: "45%" }}>
                                  <i className={`fa-solid fa-chevron-${reminderHistoryOpen ? "down" : "right"} me-2`} />Date / Time
                                </th>
                                <th className="text-primary fw-semibold">Reminder History</th>
                              </tr>
                            </thead>
                            {reminderHistoryOpen && (
                              <tbody>
                                {[...event.reminderLog].reverse().map((entry, i) => (
                                  <tr key={i}>
                                    <td className="text-muted text-nowrap">{new Date(entry.sentAt).toLocaleString()}</td>
                                    <td>
                                      <i className={
                                        entry.type === "SMS" ? "fa-solid fa-comment-sms me-2"
                                          : entry.type === "WA" ? "fa-brands fa-whatsapp me-2"
                                          : "fa-solid fa-envelope me-2"
                                      } />{entry.type === "WA" ? "WhatsApp" : entry.type} reminder sent
                                      {entry.interval === "1Day" ? " (1 day before)" : entry.interval === "Sunday" ? " (Sunday for the week)" : ""}
                                      {entry.status ? ` — ${entry.status}` : ""}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            )}
                          </Table>
                        </div>
                      )}

                      {/* Intake form audit log */}
                      {intakeSession?.auditLog?.length > 0 && (
                        <div className="px-2 pb-2">
                          <Table bordered responsive size="sm" className="mb-0 align-middle">
                            <thead
                              style={{ cursor: "pointer", userSelect: "none" }}
                              onClick={() => setIntakeHistoryOpen((o) => !o)}
                            >
                              <tr>
                                <th className="text-primary fw-semibold" style={{ width: "45%" }}>
                                  <i className={`fa-solid fa-chevron-${intakeHistoryOpen ? "down" : "right"} me-2`} />Date / Time
                                </th>
                                <th className="text-primary fw-semibold">
                                  Intake Form History &nbsp;·&nbsp;
                                  <span className={
                                    intakeSession.status === "SUBMITTED" ? "text-success" :
                                    intakeSession.status === "EXPIRED" ? "text-danger" : "text-warning"
                                  }>
                                    {intakeSession.status}
                                  </span>
                                  {intakeSession.deliveryMethod && (
                                    <span className="text-muted fw-normal"> · {intakeSession.deliveryMethod.toUpperCase()}</span>
                                  )}
                                </th>
                              </tr>
                            </thead>
                            {intakeHistoryOpen && (
                              <tbody>
                                {[...intakeSession.auditLog].reverse().map((entry, i) => (
                                  <tr key={i}>
                                    <td className="text-muted text-nowrap">{new Date(entry.timestamp).toLocaleString()}</td>
                                    <td>
                                      {({
                                        CREATED: "Link created",
                                        OPENED: "Link opened by patient",
                                        SUBMIT_ATTEMPT: "Submission attempted",
                                        SUBMITTED: "Form submitted",
                                        LOCKED: "Session locked",
                                        EXPIRED: "Link expired",
                                      }[entry.action] || entry.action)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            )}
                          </Table>
                        </div>
                      )}

                      {/* Server result */}
                      {intakeSession?.serverOutcome && (
                        <div className="px-2 pb-2">
                          <Table bordered responsive size="sm" className="mb-0 align-middle">
                            <thead
                              style={{ cursor: "pointer", userSelect: "none" }}
                              onClick={() => setIntakeOutcomeOpen((o) => !o)}
                            >
                              <tr>
                                <th className="text-primary fw-semibold" style={{ width: "45%" }}>
                                  <i className={`fa-solid fa-chevron-${intakeOutcomeOpen ? "down" : "right"} me-2`} />Server Result
                                </th>
                                <th className={`fw-semibold ${({
                                  CREATED: "text-success",
                                  MATCHED: "text-success",
                                  REVIEW_REQUIRED: "text-warning",
                                  FAILED: "text-danger",
                                }[intakeSession.serverOutcome.resolution] || "text-primary")}`}>
                                  {{
                                    CREATED: "Patient & account created",
                                    MATCHED: "Matched to existing record",
                                    REVIEW_REQUIRED: "Sent to review queue",
                                    FAILED: "Processing failed",
                                  }[intakeSession.serverOutcome.resolution] || intakeSession.serverOutcome.resolution}
                                  {/* Temporarily hidden (SHOW_REVIEW_QUEUE_LINK) — the
                                      intake-review UI needs refinement before we surface
                                      navigation to it from the appointment history. */}
                                  {SHOW_REVIEW_QUEUE_LINK &&
                                    intakeSession.serverOutcome.resolution === "REVIEW_REQUIRED" &&
                                    intakeSession.serverOutcome.reviewId && (
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="ms-2"
                                        onClick={(e) => {
                                          // Header row toggles the collapse — don't let
                                          // the button click flip it while navigating.
                                          e.stopPropagation();
                                          handleClose();
                                          navigate(
                                            `/intake-reviews?reviewId=${intakeSession.serverOutcome.reviewId}`
                                          );
                                        }}
                                      >
                                        <i className="fa-light fa-file-signature me-2" />
                                        Open Review Queue
                                      </Button>
                                    )}
                                </th>
                              </tr>
                            </thead>
                            {intakeOutcomeOpen && (intakeSession.serverOutcome.reviewReason || intakeSession.serverOutcome.patientId || intakeSession.serverOutcome.accountId || intakeSession.serverOutcome.reviewId) && (
                              <tbody>
                                {intakeSession.serverOutcome.reviewReason && (
                                  <tr>
                                    <td className="text-muted">Reason</td>
                                    <td>
                                      {{
                                        FAMILYCHECK_FAILED: "Family Check could not be completed",
                                        FAMILYCHECK_NO_DEPENDANTS: "No dependants found on medical aid",
                                        DUPLICATE_PATIENT_ID: "Patient ID number already exists",
                                        DUPLICATE_MEDICAL_AID_NUMBER: "Medical aid number already registered",
                                        DUPLICATE_PATIENT_DOB_EMAIL: "Duplicate DOB and email match found",
                                        PATIENT_ALREADY_LINKED: "Patient already linked to this account",
                                        NO_PATIENT_MATCH: "Could not find matching patient record",
                                        MULTIPLE_PATIENT_MATCHES: "Multiple patient records found",
                                        NO_ACCOUNT_MATCH: "Could not find matching account",
                                        MULTIPLE_ACCOUNT_MATCHES: "Multiple accounts found",
                                        DUPLICATE_ACCOUNT_ID: "Account ID number already exists",
                                      }[intakeSession.serverOutcome.reviewReason] || intakeSession.serverOutcome.reviewReason}
                                    </td>
                                  </tr>
                                )}
                                {intakeSession.serverOutcome.patientId && (
                                  <tr><td className="text-muted">Patient ID</td><td>{String(intakeSession.serverOutcome.patientId)}</td></tr>
                                )}
                                {intakeSession.serverOutcome.accountId && (
                                  <tr><td className="text-muted">Account ID</td><td>{String(intakeSession.serverOutcome.accountId)}</td></tr>
                                )}
                                {intakeSession.serverOutcome.reviewId && (
                                  <tr><td className="text-muted">Review ID</td><td>{String(intakeSession.serverOutcome.reviewId)}</td></tr>
                                )}
                              </tbody>
                            )}
                          </Table>
                        </div>
                      )}

                      {/* Consent record */}
                      {intakeSession?.submittedData?.consent && (
                        <div className="px-2 pb-2">
                          <Table bordered responsive size="sm" className="mb-0 align-middle">
                            <thead
                              style={{ cursor: "pointer", userSelect: "none" }}
                              onClick={() => setIntakeConsentOpen((o) => !o)}
                            >
                              <tr>
                                <th className="text-primary fw-semibold" style={{ width: "45%" }}>
                                  <i className={`fa-solid fa-chevron-${intakeConsentOpen ? "down" : "right"} me-2`} />Consent Record
                                </th>
                                <th className={`fw-semibold ${intakeSession.submittedData.consent.agreed ? "text-success" : "text-danger"}`}>
                                  <i className={`fa-solid ${intakeSession.submittedData.consent.agreed ? "fa-circle-check" : "fa-circle-xmark"} me-2`} />
                                  {intakeSession.submittedData.consent.agreed ? "Consent given" : "No consent"}
                                  {intakeSession.submittedData.consent.isProxy && (
                                    <span className="text-warning fw-normal"> · Proxy</span>
                                  )}
                                </th>
                              </tr>
                            </thead>
                            {intakeConsentOpen && (
                              <tbody>
                                <tr>
                                  <td className="text-muted">Consent Given</td>
                                  <td>
                                    {intakeSession.submittedData.consent.agreed
                                      ? <span className="text-success fw-semibold"><i className="fa-solid fa-circle-check me-2" />Yes</span>
                                      : <span className="text-danger fw-semibold"><i className="fa-solid fa-circle-xmark me-2" />No</span>}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="text-muted">Submitted by</td>
                                  <td>
                                    {intakeSession.submittedData.consent.isProxy
                                      ? <span className="text-warning fw-semibold">Proxy</span>
                                      : "Self"}
                                  </td>
                                </tr>
                                {intakeSession.submittedData.consent.isProxy && intakeSession.submittedData.consent.proxyNote && (
                                  <tr>
                                    <td className="text-muted">Proxy Note</td>
                                    <td>{intakeSession.submittedData.consent.proxyNote}</td>
                                  </tr>
                                )}
                                {intakeSession.clientMeta?.ipLastSeen && (
                                  <tr>
                                    <td className="text-muted">IP Address</td>
                                    <td className="font-monospace">{intakeSession.clientMeta.ipLastSeen}</td>
                                  </tr>
                                )}
                                {(intakeSession.submittedAt || intakeSession.auditLog?.find(e => e.action === "SUBMITTED")?.timestamp) && (
                                  <tr>
                                    <td className="text-muted">Submitted At</td>
                                    <td>
                                      {new Date(
                                        intakeSession.submittedAt ||
                                        intakeSession.auditLog.find(e => e.action === "SUBMITTED").timestamp
                                      ).toLocaleString()}
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            )}
                          </Table>
                        </div>
                      )}

                      {/* Submitted form data */}
                      {intakeSession?.submittedData && (
                        <div className="px-2 pb-2">
                          <Table bordered responsive size="sm" className="mb-0 align-middle">
                            <thead
                              style={{ cursor: "pointer", userSelect: "none" }}
                              onClick={() => setIntakeDataOpen((o) => !o)}
                            >
                              <tr>
                                <th className="text-primary fw-semibold" colSpan={2}>
                                  <i className={`fa-solid fa-chevron-${intakeDataOpen ? "down" : "right"} me-2`} />Submitted Form Data
                                </th>
                              </tr>
                            </thead>
                          </Table>
                          {intakeDataOpen && [
                            intakeSession.submittedData.medicalAid && {
                              title: "Medical Aid",
                              rows: [
                                ["Scheme", intakeSession.submittedData.medicalAid.schemeName],
                                ["Plan", intakeSession.submittedData.medicalAid.planName],
                                ["Option", intakeSession.submittedData.medicalAid.optionName],
                                ["Number", intakeSession.submittedData.medicalAid.medicalAidNumber],
                                ["Dependant Code", intakeSession.submittedData.medicalAid.dependantCode],
                              ],
                            },
                            intakeSession.submittedData.patient && {
                              title: "Patient",
                              rows: [
                                ["Name", [intakeSession.submittedData.patient.firstName, intakeSession.submittedData.patient.lastName].filter(Boolean).join(" ")],
                                ["ID Number", intakeSession.submittedData.patient.idNumber],
                                ["Date of Birth", intakeSession.submittedData.patient.dob],
                                ["Gender", { "1": "Male", "2": "Female" }[intakeSession.submittedData.patient.gender] || intakeSession.submittedData.patient.gender],
                                ["Phone", intakeSession.submittedData.patient.tel],
                                ["Email", intakeSession.submittedData.patient.email],
                                ["Language", intakeSession.submittedData.patient.language],
                              ],
                            },
                            intakeSession.submittedData.accountHolder && {
                              title: "Account Holder",
                              rows: [
                                ["Name", [intakeSession.submittedData.accountHolder.firstName, intakeSession.submittedData.accountHolder.lastName].filter(Boolean).join(" ")],
                                ["ID Number", intakeSession.submittedData.accountHolder.idNumber],
                                ["Date of Birth", intakeSession.submittedData.accountHolder.dob],
                                ["Phone", intakeSession.submittedData.accountHolder.tel],
                                ["Email", intakeSession.submittedData.accountHolder.email],
                                ["Address", [intakeSession.submittedData.accountHolder.address1, intakeSession.submittedData.accountHolder.city, intakeSession.submittedData.accountHolder.province, intakeSession.submittedData.accountHolder.postalCode].filter(Boolean).join(", ")],
                              ],
                            },
                            intakeSession.submittedData.contact && {
                              title: "Contact",
                              rows: [
                                ["Phone", intakeSession.submittedData.contact.tel],
                                ["Email", intakeSession.submittedData.contact.email],
                                ["Language", intakeSession.submittedData.contact.language],
                                ["Address", [intakeSession.submittedData.contact.address1, intakeSession.submittedData.contact.city, intakeSession.submittedData.contact.province, intakeSession.submittedData.contact.postalCode].filter(Boolean).join(", ")],
                              ],
                            },
                          ].filter(Boolean).map(({ title, rows }) => (
                            <Table key={title} bordered responsive size="sm" className="mb-2 align-middle">
                              <thead>
                                <tr>
                                  <th className="text-primary fw-semibold" colSpan={2}>{title}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {rows.filter(([, v]) => v).map(([label, val]) => (
                                  <tr key={label}>
                                    <td className="text-muted" style={{ width: "40%" }}>{label}</td>
                                    <td>{val}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          ))}
                        </div>
                      )}

                      {/* Intake link */}
                      {intakeSession?.link && (
                        <div className="p-2 d-flex align-items-center gap-2 border-top">
                          <Form.Control
                            size="sm"
                            readOnly
                            value={intakeSession.link}
                            className="appt-readonly"
                            style={{ fontSize: "0.72rem" }}
                          />
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            style={{ whiteSpace: "nowrap" }}
                            onClick={() => navigator.clipboard?.writeText(intakeSession.link)}
                          >
                            Copy
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-muted small mb-2">No history for this appointment.</p>
                  )}
                  {intakeEnabled && statusTypeOf(appointment.status, appointment.practiceId) === "NewPatient" && (!intakeSession || ["CREATED", "OPENED"].includes(intakeSession.status)) && (
                    <>
                      <Form.Check
                        type="checkbox"
                        label="Send Intake form via email"
                        className="mt-1"
                        checked={sendIntakeEmail}
                        disabled={!isEditMode || !appointment.patientEmail || intakeSending}
                        title={!appointment.patientEmail ? "Patient email required" : ""}
                        onChange={(e) => setSendIntakeEmail(e.target.checked)}
                      />
                      {intakeSending && (
                        <div className="text-muted small mt-1">
                          <span className="spinner-border spinner-border-sm me-1" />Sending intake link...
                        </div>
                      )}
                    </>
                  )}
                </Col>
              </Row>
              </>
            )}
          </Form>
          </>)}
        </Modal.Body>
        {isEditMode && historyView === 'none' && (
          <Modal.Footer>
            <Button
              variant="primary"
              onClick={handleSaveAppointment}
              style={{ minWidth: 140 }}
              disabled={isLoading || isUpdatingStatus}
            >
              {isLoading ? (
                <Spinner size="sm" animation="border" />
              ) : (
                <>
                  <i className="fa-regular fa-floppy-disk me-2" />
                  Save
                </>
              )}
            </Button>
            <Button
              variant="outline-secondary"
              onClick={handleClose}
              style={{ minWidth: 140 }}
              disabled={isLoading || isUpdatingStatus}
            >
              Cancel
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {modalShow && (
        <AddPatientSteps
          show={modalShow}
          onHide={() => setModalShow(false)}
          patientFirstName={modalData?.patientFirstName}
          patientLastName={modalData?.patientLastName}
          patientEmail={modalData?.patientEmail}
          patientCellNumber={modalData?.patientPhone}
          appointmentId={modalData?.appointmentId}
          onSaved={() => {
            setModalShow(false);
            setRefreshTick((t) => t + 1);
          }}
        />
      )}

      <AppToast
        show={toast.show}
        onClose={hideToast}
        variant={toast.variant}
        message={toast.message}
      />
    </div>
  );
}
